import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { InvoiceStanding } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceStanding";
import { InvoiceType } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceType";
import moment from "moment";
import { formatPostalCode } from "@/Helpers/formatPostalCode";

/**
 * Status names come from the handbook's Metric Glossary ("Customer invoice
 * statuses") - the definitive naming source. "Open" (not "Unpaid") because
 * customers read "unpaid" as money being chased when it simply isn't due yet.
 *
 * Whether an invoice is overdue is decided by the server (`invoice.standing`),
 * not by comparing dates here. That is the whole point: the screen used to
 * compute it itself and disagreed with the rule that gates booking, so a
 * disputed invoice read as "Overdue" in red while nothing was blocked.
 *
 * "Failed" and "Refund" are still derived here, because they are facts about
 * the invoice rather than a second opinion about a date.
 */
export function invoiceStatusDescription(invoice: Invoice) {
  const isRefund =
    invoice.invoiceType === InvoiceType.AdditionalChargeRefund ||
    invoice.invoiceType === InvoiceType.ShipmentRefund;

  switch (invoice.standing) {
    case InvoiceStanding.Voided:
      return "Voided";
    case InvoiceStanding.Paid:
      return isRefund ? "Refund" : "Paid";
    case InvoiceStanding.PaymentSent:
      return "Payment Sent";
    case InvoiceStanding.Disputed:
      return "Disputed";
    case InvoiceStanding.Overdue:
      return invoice.lastPaymentAttemptFailed ? "Failed" : "Overdue";
    case InvoiceStanding.Open:
      return invoice.lastPaymentAttemptFailed ? "Failed" : "Open";
  }
}

export function formatIdentifierDateLine(invoice: Invoice) {
  if (!invoice.invoiceIdentifier) {
    return moment(invoice.invoiceDate).format("Do MMMM YYYY");
  }

  return `${invoice.invoiceIdentifier} - ${moment(invoice.invoiceDate).format(
    "Do MMMM YYYY",
  )}`;
}

interface ShipmentDisplayDetails {
  pickupReferenceNumber?: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  pickupCity: string;
  deliveryCity: string;
  pickupBusinessName: string;
  deliveryBusinessName: string;
}

/**
 * Returns display details for a given shipment, including reference numbers,
 * city/state information, and business names or formatted postal codes for pickup and delivery locations.
 *
 * @param shipment - The shipment object containing pickup and delivery location details.
 * @param raw - If true, returns only the formatted postal code for locations without a business name;
 *              otherwise, returns a descriptive string with the postal code. Defaults to false.
 * @returns An object containing pickup and delivery reference numbers, city/state strings,
 *          and business names or formatted postal codes for both pickup and delivery.
 */
export function getShipmentDisplayDetails(
  shipment?: Shipment,
  raw: boolean = false,
): ShipmentDisplayDetails {
  if (!shipment) {
    return {
      pickupReferenceNumber: "",
      deliveryReferenceNumber: "",
      pickupCity: "",
      deliveryCity: "",
      pickupBusinessName: "",
      deliveryBusinessName: "",
    };
  }

  const {
    pickupReferenceNumber,
    deliveryReferenceNumber,
    pickupLocation,
    deliveryLocation,
  } = shipment;

  const pickupCity = `${pickupLocation.address.city}, ${pickupLocation.address.stateOrProvinceCode}`;
  const deliveryCity = `${deliveryLocation.address.city}, ${deliveryLocation.address.stateOrProvinceCode}`;

  const pickupBusinessName =
    pickupLocation.businessName ??
    (pickupLocation.address.postalCode && raw
      ? formatPostalCode(pickupLocation.address.postalCode)
      : pickupLocation.address.postalCode
        ? `Picking up from : ${formatPostalCode(
            pickupLocation.address.postalCode,
          )}`
        : "");

  const deliveryBusinessName =
    deliveryLocation.businessName ??
    (deliveryLocation.address.postalCode && raw
      ? formatPostalCode(deliveryLocation.address.postalCode)
      : deliveryLocation.address.postalCode
        ? `Delivering to : ${formatPostalCode(
            deliveryLocation.address.postalCode,
          )}`
        : "");

  return {
    pickupReferenceNumber,
    deliveryReferenceNumber,
    pickupCity,
    deliveryCity,
    pickupBusinessName,
    deliveryBusinessName,
  };
}
